export function ConvertObjectToFormData(object: any) {
    const formData = new FormData()
    Object.keys(object).forEach((key) => {
        object[key] !== null && formData.append(key, object[key])
    })
    return formData
}

export const openInNewTab = (url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer')
}

export const convertToSearchString = (text?: string) => {
    if (!text) return ''
    return text
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
}

export const getNumberRoundedInFormattedString = (number: number) => {
    return (
        Math.round(number)
            .toLocaleString('cs')
            .replace(/\u00A0/g, ' ') + ' Kč'
    )
}

/**
 * @param timeSpan timeSpan in format 1.5:30:00
 * @returns timeSpan in format 29:30:00
 */
export const getFormattedTimeSpanFromString = (timeSpan: string) => {
    const splitedValue = timeSpan.split(':')
    const hours = splitedValue[0].includes('.')
        ? Number(splitedValue[0].split('.')[0]) * 24 + Number(splitedValue[0].split('.')[1])
        : splitedValue[0]
    const minutes = Number(splitedValue[1])
    return hours + ':' + minutes + ':00'
}

/**
 * @param timeSpan timeSpan in format 3:2:00
 * @returns timeSpan in format 03:02:00
 */
export const addLeadingZerosToTimeSpan = (timeSpan: string) => {
    return timeSpan.split(':').length === 2
        ? timeSpan + ':00' // add seconds
        : timeSpan
              .split(':')
              .map((item) => (!item ? '00' : item.length === 1 ? '0' + item : item))
              .join(':')
}

/**
 * @param timeSpan timeSpan in format 29:30:00
 * @returns timeSpan in format 1.5:30:00
 */
export const getTimeSpanFromFormattedTimeSpan = (timeSpan: string) => {
    const splitedValue = timeSpan.split(':')
    let hours = splitedValue[0].includes('.')
        ? Number(splitedValue[0].split('.')[0]) * 24 + Number(splitedValue[0].split('.')[1])
        : Number(splitedValue[0])
    let minutes = Number(splitedValue[1])
    let hoursString = ''
    if (minutes > 60) {
        hours += Math.floor(minutes / 60)
        minutes = minutes % 60
    }
    if (hours > 24) {
        hoursString = Math.floor(hours / 24) + '.' + (hours % 24)
    } else hoursString = hours.toString()
    return addLeadingZerosToTimeSpan(hoursString + ':' + minutes + ':00')
}

/**
 * @param timeSpan timeSpan in format 1.5:30:00
 * @returns timeSpan in format 29h 30m
 */
export const getFormattedTimeSpanToReadableText = (timeSpan: string) => {
    const splitedValue = timeSpan.split(':')
    const hours = splitedValue[0].includes('.')
        ? Number(splitedValue[0].split('.')[0]) * 24 + Number(splitedValue[0].split('.')[1])
        : splitedValue[0]
    const minutes = Number(splitedValue[1])
    return Number(hours) + 'h ' + minutes + 'm'
}

/**
 * @param timeSpan timeSpan in format 1.5:30:00
 * @returns number of seconds - 91800
 * @description 1.5:30:00 = 91800 seconds
 * 1.5 = 36 hours
 * 30 = 30 minutes
 * 00 = 0 seconds
 * 36 * 3600 + 30 * 60 + 0 = 91800 seconds
 */
export const getSecondsFromTimeSpan = (timeSpan: string) => {
    const splitedValue = timeSpan.split(':')
    const hours = splitedValue[0].includes('.')
        ? Number(splitedValue[0].split('.')[0]) * 24 + Number(splitedValue[0].split('.')[1])
        : Number(splitedValue[0])
    const minutes = Number(splitedValue[1])
    const seconds = Number(splitedValue[2])
    return hours * 3600 + minutes * 60 + seconds
}

/**
 * @param seconds number of seconds - 91800
 * @returns timeSpan in format 1.5:30:00
 * @description 91800 seconds = 1.5:30:00
 * 91800 / 3600 = 25.5 hours
 * 25.5 - 25 = 0.5 hours
 * 0.5 * 60 = 30 minutes
 * 25 hours + 30 minutes = 1.5:30:00
 */
export const getFormattedTimeSpanFromSeconds = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds - hours * 3600) / 60)
    return addLeadingZerosToTimeSpan(hours + ':' + minutes + ':' + 0)
}

export const convertTimeToLocaleTime = (date: Date) => {
    const newDate = new Date(date)
    const hoursDiff = newDate.getHours() - newDate.getTimezoneOffset() / 60
    const minutesDiff = (newDate.getMinutes() - newDate.getTimezoneOffset()) % 60
    newDate.setHours(hoursDiff)
    newDate.setMinutes(minutesDiff)
    return newDate
}

/**
 * removeTimezone function is used to remove timezone from date
 * @param date date to remove timezone from
 * @returns date without timezone in format yyyy-mm-ddThh:mm:ss
 */
export const removeTimezone = (date: Date) => {
    const utcTime = new Date(date).toISOString()
    const localTime = new Date(utcTime)
    const offset = localTime.getTimezoneOffset()
    localTime.setMinutes(localTime.getMinutes() - offset)
    return localTime.toISOString().slice(0, -1)
}

/**
 * handleLinkClick function is used to open links in new tab or current tab (left click - current tab, middle mouse click or ctrl/cmd + left click - new tab)
 * @param event onMouseDown event
 * @param route route to open
 * @param onCurrentTab function to execute when opening in current tab (optional) - used when you need to execute some function before opening in current tab
 */
export const handleLinkClick = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement | HTMLSpanElement, MouseEvent>,
    route: string,
    onCurrentTab?: () => void
) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (event.metaKey || event.button === 1 || event.ctrlKey) {
        openInNewTab(route)
    } else {
        onCurrentTab ? onCurrentTab() : window.open(route, '_self')
    }
}

export const limitCharactersInText = (text: string, limit: number) => {
    if (text == null) {
        return ''
    }
    return text.length > limit ? text.slice(0, limit) + '...' : text
}

export const formatTwoDates = (date1: Date, date2: Date) => {
    const date1String = date1.toLocaleDateString()
    const date2String = date2.toLocaleDateString()
    const date1Time = date1.toLocaleTimeString().slice(0, -3)
    const date2Time = date2.toLocaleTimeString().slice(0, -3)
    return date1String === date2String
        ? date1String + ' ' + date1Time + ' - ' + date2Time
        : date1String + ' ' + date1Time + ' - ' + date2String + ' ' + date2Time
}

export const calculateTimeDifferenceInTimeSpan = (date1: Date, date2: Date) => {
    if (!date1 || !date2) return ''
    const start = new Date(date1)
    const end = new Date(date2)

    // Set seconds and milliseconds to 0 for both dates
    start.setSeconds(0, 0)
    end.setSeconds(0, 0)

    // Calculate the time difference in milliseconds
    const timeDiff = end.getTime() - start.getTime()

    // Calculate the time difference in hours and minutes
    const hours = Math.floor(timeDiff / (1000 * 60 * 60))
    let minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60))

    // Check if seconds component is greater than or equal to 30 and round up minutes if necessary
    if (timeDiff % (1000 * 60) >= 30 * 1000) {
        minutes++
    }

    return getTimeSpanFromFormattedTimeSpan(addLeadingZerosToTimeSpan(`${hours}:${minutes}:00`))
}
